/**
 * SolarParametersPanel Component
 *
 * Collapsible form exposing the 5 SolarParams fields driving the PV
 * model (panel_watts, num_panels, tilt_deg, azimuth_deg,
 * performance_ratio) plus a "Combine with battery" checkbox that
 * layers a BatteryParams preset on top of the solar dispatch.
 *
 * Edits are staged locally and only dispatched to the simulator when
 * the user clicks "Recompute", so mid-edit form state doesn't trigger
 * waste runs of the 10-20s solar+battery DP on every keystroke.
 *
 * num_panels is hard-capped at MAX_PANELS (40) — the available roof
 * area at Dalsholm. Changing num_panels does NOT demote a preset to
 * "Custom" (see identifySolarPreset).
 */

import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react'
import { DEFAULT_SOLAR } from '@/hooks/useSolarSimulation'
import {
  SOLAR_PRESETS,
  CUSTOM_SOLAR_PRESET_ID,
  identifySolarPreset,
  getSolarPreset,
  ratedKwp,
  MAX_PANELS,
} from '@/lib/solarPresets'
import {
  BATTERY_PRESETS,
  CUSTOM_PRESET_ID as CUSTOM_BATTERY_PRESET_ID,
  identifyPreset as identifyBatteryPreset,
  getPreset as getBatteryPreset,
  getCheapestBatteryPreset,
} from '@/lib/batteryPresets'

const SOLAR_FIELDS = [
  { key: 'panel_watts', label: 'Panel wattage (STC)', unit: 'W', step: 5, min: 100 },
  { key: 'tilt_deg', label: 'Tilt (metadata only)', unit: '°', step: 1, min: 0, max: 90 },
  { key: 'azimuth_deg', label: 'Azimuth (0 = S)', unit: '°', step: 5, min: -180, max: 180 },
  { key: 'performance_ratio', label: 'Performance ratio', unit: '', step: 0.01, min: 0.1, max: 1 },
]

export default function SolarParametersPanel({
  solar,
  battery,           // null = solar-only
  onApply,           // ({ solar, battery }) => void
  isLoading = false,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [draftSolar, setDraftSolar] = useState(solar)
  const [draftBattery, setDraftBattery] = useState(battery)

  // Keep drafts in sync if parent props change externally.
  useEffect(() => { setDraftSolar(solar) }, [solar])
  useEffect(() => { setDraftBattery(battery) }, [battery])

  const dirty = JSON.stringify(draftSolar) !== JSON.stringify(solar)
    || JSON.stringify(draftBattery) !== JSON.stringify(battery)

  const draftSolarPresetId = useMemo(() => identifySolarPreset(draftSolar), [draftSolar])
  const selectedSolarPreset = draftSolarPresetId === CUSTOM_SOLAR_PRESET_ID
    ? null
    : getSolarPreset(draftSolarPresetId)

  const draftBatteryPresetId = useMemo(
    () => draftBattery ? identifyBatteryPreset(draftBattery) : null,
    [draftBattery]
  )
  const selectedBatteryPreset = !draftBatteryPresetId || draftBatteryPresetId === CUSTOM_BATTERY_PRESET_ID
    ? null
    : getBatteryPreset(draftBatteryPresetId)

  const draftKwp = ratedKwp(draftSolar.panel_watts ?? 0, draftSolar.num_panels ?? 0)

  const handleSolarChange = (key, raw) => {
    let next = raw === '' ? '' : Number(raw)
    if (key === 'num_panels' && typeof next === 'number' && next > MAX_PANELS) {
      next = MAX_PANELS
    }
    setDraftSolar(prev => ({ ...prev, [key]: next }))
  }

  const handleSolarPresetChange = (id) => {
    if (id === CUSTOM_SOLAR_PRESET_ID) return
    const preset = getSolarPreset(id)
    if (preset) {
      // Preserve the user's current num_panels choice across preset
      // changes — they're comparing panel types, not re-sizing arrays.
      const currentNumPanels = draftSolar.num_panels ?? preset.params.num_panels
      setDraftSolar({ ...preset.params, num_panels: Math.min(currentNumPanels, MAX_PANELS) })
    }
  }

  const handleBatteryToggle = (checked) => {
    if (checked) {
      // Default to the lowest £/kWh preset — most favourable payback
      // hook on first toggle; user can swap via the preset dropdown.
      setDraftBattery({ ...getCheapestBatteryPreset().params })
    } else {
      setDraftBattery(null)
    }
  }

  const handleBatteryPresetChange = (id) => {
    if (id === CUSTOM_BATTERY_PRESET_ID) return
    const preset = getBatteryPreset(id)
    if (preset) setDraftBattery({ ...preset.params })
  }

  const handleApply = () => {
    const coercedSolar = Object.fromEntries(
      Object.entries(draftSolar).map(([k, v]) => [k, Number.isFinite(v) ? v : solar[k]])
    )
    setDraftSolar(coercedSolar)
    onApply({ solar: coercedSolar, battery: draftBattery })
  }

  const handleReset = () => {
    setDraftSolar(DEFAULT_SOLAR)
    setDraftBattery(null)
  }

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardContent className="py-4">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 w-full text-left font-medium text-neutral-900 hover:text-neutral-700 transition-colors"
            >
              {open
                ? <ChevronDown className="h-4 w-4 text-neutral-500" />
                : <ChevronRight className="h-4 w-4 text-neutral-500" />}
              <span>Solar parameters</span>
              <span className="text-xs text-neutral-500 font-normal ml-2">
                {selectedSolarPreset ? selectedSolarPreset.name : 'Custom'} ·{' '}
                {(solar.num_panels ?? 0)} panels ·{' '}
                {ratedKwp(solar.panel_watts ?? 0, solar.num_panels ?? 0).toFixed(1)} kWp
                {battery && (
                  <> · +battery {battery.capacity_kwh_usable} kWh</>
                )}
              </span>
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="pt-4">
            {/* Solar preset picker + shared panel count */}
            <div className="mb-6 pb-4 border-b border-neutral-200">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs text-neutral-600">Compare panel</Label>
                  <Select
                    value={draftSolarPresetId}
                    onValueChange={handleSolarPresetChange}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="md:w-96">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOLAR_PRESETS.map(preset => {
                        // Defensive: num_panels can transiently be '' or NaN
                        // while the user is editing the input.
                        const rawN = Number(draftSolar.num_panels)
                        const n = Number.isFinite(rawN) && rawN > 0 ? rawN : 40
                        const kwp = (preset.panel_watts * n) / 1000
                        const scale = n / 40
                        const minPrice = preset.price_gbp_min * scale
                        const maxPrice = preset.price_gbp_max * scale
                        const pricePerKwp = Math.round(
                          ((preset.price_gbp_min + preset.price_gbp_max) / 2) / preset.rated_kwp_at_40
                        )
                        return (
                          <SelectItem
                            key={preset.id}
                            value={preset.id}
                            textValue={preset.name}
                          >
                            <div className="flex flex-col items-start">
                              <span className="font-medium">{preset.name}</span>
                              <span className="text-xs text-neutral-500">
                                {kwp.toFixed(1)} kWp @ {n} ·{' '}
                                £{(minPrice / 1000).toFixed(0)}
                                -{(maxPrice / 1000).toFixed(0)}k installed ·{' '}
                                ~£{pricePerKwp}/kWp
                              </span>
                            </div>
                          </SelectItem>
                        )
                      })}
                      {draftSolarPresetId === CUSTOM_SOLAR_PRESET_ID && (
                        <SelectItem value={CUSTOM_SOLAR_PRESET_ID} disabled textValue="Custom">
                          Custom (edited)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 md:w-44">
                  <Label htmlFor="sp-num-panels" className="text-xs text-neutral-600">
                    Number of panels
                    <span className="text-neutral-400 ml-1">max {MAX_PANELS}</span>
                  </Label>
                  <Input
                    id="sp-num-panels"
                    type="number"
                    value={draftSolar.num_panels ?? ''}
                    onChange={(e) => handleSolarChange('num_panels', e.target.value)}
                    step={1}
                    min={1}
                    max={MAX_PANELS}
                    disabled={isLoading}
                    className="h-9"
                  />
                </div>
              </div>
              {selectedSolarPreset?.notes && (
                <p className="text-xs text-neutral-500 mt-3">
                  {selectedSolarPreset.notes}
                </p>
              )}
            </div>

            {/* Battery scenario toggle — promoted to sit with the
                top-level scenario controls rather than buried below the
                array params grid. */}
            <div className={`mb-6 pb-4 border-b border-neutral-200 rounded-lg p-4 -mt-2 transition-colors ${
              draftBattery ? 'bg-green-50 border border-green-200' : 'bg-neutral-50 border border-neutral-200'
            }`}>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="sp-battery-toggle"
                  checked={!!draftBattery}
                  onCheckedChange={handleBatteryToggle}
                  disabled={isLoading}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="sp-battery-toggle"
                    className="text-sm font-medium text-neutral-900 cursor-pointer"
                  >
                    Combine with battery
                  </Label>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Adds a perfect-foresight DP layer on top of solar dispatch.
                    PV charges the battery from surplus; battery discharges into
                    load or shifts Agile-peak grid draw. No export.
                  </p>
                </div>
              </div>

              {draftBattery && (
                <div className="mt-4 pl-7 space-y-2">
                  <Label className="text-xs text-neutral-600">Battery preset</Label>
                  <div className="flex flex-col md:flex-row md:items-start gap-3">
                    <Select
                      value={draftBatteryPresetId ?? CUSTOM_BATTERY_PRESET_ID}
                      onValueChange={handleBatteryPresetChange}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="md:w-96">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BATTERY_PRESETS.map(preset => {
                          const midPrice = (preset.price_gbp_min + preset.price_gbp_max) / 2
                          const pricePerKwh = Math.round(midPrice / preset.rated_kwh)
                          return (
                            <SelectItem key={preset.id} value={preset.id}>
                              <div className="flex flex-col items-start">
                                <span className="font-medium">{preset.name}</span>
                                <span className="text-xs text-neutral-500">
                                  {preset.rated_kwh} kWh ·{' '}
                                  £{(preset.price_gbp_min / 1000).toFixed(0)}
                                  -{(preset.price_gbp_max / 1000).toFixed(0)}k hardware ·{' '}
                                  ~£{pricePerKwh}/kWh
                                </span>
                              </div>
                            </SelectItem>
                          )
                        })}
                        {draftBatteryPresetId === CUSTOM_BATTERY_PRESET_ID && (
                          <SelectItem value={CUSTOM_BATTERY_PRESET_ID} disabled>
                            Custom (edited)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedBatteryPreset?.notes && (
                      <p className="text-xs text-neutral-500 md:flex-1">
                        {selectedBatteryPreset.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Solar params grid */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                Array configuration · {draftKwp.toFixed(1)} kWp rated
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {SOLAR_FIELDS.map(field => (
                  <div key={field.key} className="space-y-1">
                    <Label htmlFor={`sp-${field.key}`} className="text-xs text-neutral-600">
                      {field.label}{field.unit && ` (${field.unit})`}
                    </Label>
                    <Input
                      id={`sp-${field.key}`}
                      type="number"
                      value={draftSolar[field.key] ?? ''}
                      onChange={(e) => handleSolarChange(field.key, e.target.value)}
                      step={field.step}
                      min={field.min}
                      max={field.max}
                      disabled={isLoading}
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-500">
                Tilt/azimuth are metadata only in v1 — the performance ratio bundles
                tilt uplift with wiring/inverter/soiling losses. 0.80-0.85 is the
                typical UK C&I band applied to GHI.
              </p>
            </div>

            <div className="flex items-center justify-between gap-4 mt-6 pt-4 border-t border-neutral-200">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={isLoading}
                className="text-xs"
              >
                <RotateCcw className="h-3 w-3 mr-1.5" />
                Reset to Jinko defaults
              </Button>
              <div className="flex items-center gap-3">
                {dirty && (
                  <span className="text-xs text-neutral-500">Unsaved changes</span>
                )}
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={!dirty || isLoading}
                >
                  {isLoading ? 'Computing…' : 'Recompute'}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  )
}
